import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { LogOut, LayoutDashboard, Shield } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../api/endpoints/auth';
import { BACKOFFICE_SECTIONS } from '../../lib/sections';

/**
 * Shell del backoffice SureKeep — portado del design system del mock (mockups/_shared/layout.css):
 * sidebar 224px (bg card), header 48px con marca navy, nav con item activo en `accent`, topbar y
 * contenido full-width. Fuente de verdad de diseño: repo surekeep/mockups.
 */
export function SurekeepShell({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const clear = useAuthStore((s) => s.clear);

    const handleLogout = async (): Promise<void> => {
        if (user) {
            await authApi
                .logout({ cognitoSub: user.cognitoSub, tenantId: user.tenantId })
                .catch(() => undefined);
        }
        clear();
        await navigate({ to: '/login' });
    };

    // .sidebar-item del mock: px-2 py-[5px], radius 6px, texto foreground/80, hover/active = accent.
    const navItem =
        'flex items-center gap-2 rounded-md px-2 py-[5px] text-[12.5px] font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground';
    const navItemActive = 'bg-accent font-semibold text-accent-foreground';

    return (
        <div className="flex h-screen overflow-hidden bg-background text-foreground">
            {/* Sidebar — .sidebar (224px) */}
            <aside className="flex w-56 flex-none flex-col border-r border-border bg-card">
                {/* .sidebar-header (48px) */}
                <div className="flex h-12 items-center gap-2 border-b border-border px-4">
                    <span className="flex size-7 flex-none items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <Shield className="size-3.5" strokeWidth={2.5} aria-hidden />
                    </span>
                    <span className="text-[13px] font-semibold tracking-tight">SureKeep</span>
                </div>

                {/* .sidebar-nav */}
                <nav className="flex-1 overflow-y-auto p-2">
                    <Link
                        to="/"
                        activeOptions={{ exact: true }}
                        className={navItem}
                        activeProps={{ className: `${navItem} ${navItemActive}` }}
                    >
                        <LayoutDashboard className="size-3.5 shrink-0" aria-hidden />
                        Inicio
                    </Link>

                    <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Bases operativas
                    </div>

                    {BACKOFFICE_SECTIONS.map((section) => {
                        const Icon = section.icon;
                        return (
                            <Link
                                key={section.key}
                                to="/seccion/$key"
                                params={{ key: section.key }}
                                className={navItem}
                                activeProps={{ className: `${navItem} ${navItemActive}` }}
                            >
                                <Icon className="size-3.5 shrink-0" aria-hidden />
                                {section.label}
                            </Link>
                        );
                    })}
                </nav>

                {/* .sidebar-footer */}
                <div className="border-t border-border p-3">
                    <div className="px-1 pb-2 text-[11px] text-muted-foreground">
                        <span className="block text-[12px] font-semibold text-foreground">
                            {user?.displayName ?? 'Sesión activa'}
                        </span>
                        {user?.email ?? ''}
                    </div>
                    <button
                        type="button"
                        onClick={() => void handleLogout()}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-[5px] text-[12.5px] font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                        <LogOut className="size-3.5 shrink-0" aria-hidden />
                        Cerrar sesión
                    </button>
                </div>
            </aside>

            {/* Main — .main + .content */}
            <main className="flex flex-1 flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto bg-background px-5 py-4">{children}</div>
            </main>
        </div>
    );
}
